import { rem } from 'polished'
import * as React from 'react'
import addIcon from '$icons/add.svg'
import copyIcon from '$icons/copy.svg'
import { css, FontWeight, styled } from '$utils/theme'
import { TextFontWeight } from '$utils/theme/font'
import { Icon } from '../Icon'
import { Block } from '../Layout/Block'
import { Link } from '../Link'

interface Props extends React.ButtonHTMLAttributes<any> {
  primary?: boolean
  secondary?: boolean
  disabled?: boolean
  tertiary?: boolean
  quaternary?: boolean
  fluid?: boolean
  mini?: boolean
  bordered?: boolean
  add?: boolean
  label?: string
  weight?: TextFontWeight
  withIcon?: boolean
}

export const Button = styled.button.attrs({
  disabled: (p: Props) => p.disabled || undefined,
  type: (p: Props) => (p.add ? 'button' : 'submit'),
})<Props>`
    position: relative;
    appearance: none;
    height: ${p => rem(p.mini ? 34 : p.theme.sizes.large)};
    background-color: ${p => p.theme.colors.lightGrey};
    color: ${p => p.theme.colors.darkGrey};
    border: ${p => p.theme.colors.lightGrey} solid 2px;
    border-radius: ${p => rem(p.mini ? p.theme.sizes.tiny : 25)};

    ${p => p.theme.font.base(FontWeight.medium)};
    &:focus{
      outline: none;
    }

    &:hover {
      background-color: ${p => p.theme.colors.xlightGrey};
      border-color: ${p => p.theme.colors.xlightGrey};
      cursor: pointer;
    }

    ${p =>
      !p.mini &&
      css`
        text-transform: 'initial';
        > svg {
          width: 100%;
          height: 100%;
          vertical-align: middle;
        }
      `};

    ${p =>
      !p.fluid &&
      css`
        padding: ${rem(p.mini ? p.theme.sizes.tiny : p.theme.sizes.none)}
          ${rem(p.mini ? p.theme.sizes.base : p.theme.sizes.medium)};
      `}

    ${p =>
      p.mini &&
      css`
        line-height: 0 !important;
        ${p.theme.font.base(FontWeight.medium)};
        padding: 0 ${rem(p.theme.sizes.mini)};
        border-radius: ${rem(25)};
      `}

    ${p => (p.fluid ? 'width:100%' : 'width: auto')};

    ${p =>
      p.primary &&
      css`
        background-color: ${p.theme.colors.darkGrey};
        border-color: ${p.theme.colors.darkGrey};
        color: ${p.theme.colors.white};

        &:hover {
          background-color: ${p.theme.colors.black};
          border-color: ${p.theme.colors.black};
          cursor: pointer;
        }
        &:active {
          background-color: ${p.theme.colors.black};
          border-color: ${p.theme.colors.black};
          outline: none;
        }
      `}

    ${p =>
      p.primary &&
      p.disabled &&
      css`
        background-color: ${p.theme.colors.lightGrey};
        border-color: ${p.theme.colors.lightGrey};
        color: ${p.theme.colors.menu};

        &:hover {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};
          cursor: not-allowed;
        }
        &:active {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};
          cursor: not-allowed;
        }
      `}

    ${p =>
      p.primary &&
      p.bordered &&
      css`
        border-color: ${p.theme.colors.darkGrey};
        background-color: ${p.theme.colors.white};
        color: ${p.theme.colors.darkGrey};

        &:hover {
          background-color: ${p.theme.colors.xlightGrey};
        }
        &:active {
          background-color: ${p.theme.colors.xlightGrey};
        }
      `}

    ${p =>
      p.primary &&
      p.bordered &&
      p.disabled &&
      css`
        background-color: ${p.theme.colors.lightGrey};
        border-color: ${p.theme.colors.lightGrey};
        color: ${p.theme.colors.menu};

        &:hover {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};
          cursor: not-allowed;
        }
        &:active {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};
          cursor: not-allowed;
        }
      `}

    ${p =>
      p.secondary &&
      css`
        background-color: ${p.theme.colors.lightPink};
        border-color: ${p.theme.colors.lightPink};
        color: ${p.theme.colors.darkGrey};

        &:hover {
          background-color: ${p.theme.colors.pink};
          color: ${p.theme.colors.darkGrey};
          border-color: ${p.theme.colors.pink};
          cursor: pointer;
        }
        &:active {
          background-color: ${p.theme.colors.pink};
          color: ${p.theme.colors.darkGrey};
          border-color: ${p.theme.colors.pink};
          cursor: pointer;
          outline: none;
        }
      `}

    ${p =>
      p.secondary &&
      p.disabled &&
      css`
        background-color: ${p.theme.colors.lightGrey};
        border-color: ${p.theme.colors.lightGrey};
        color: ${p.theme.colors.menu};

        &:hover {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};
          cursor: not-allowed;
        }
        &:active {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};
          cursor: not-allowed;
        }
      `}
      ${p =>
        p.secondary &&
        p.bordered &&
        css`
          background-color: ${p.theme.colors.white};
          color: ${p.theme.colors.darkGrey};

          &:hover {
            background-color: ${p.theme.colors.xlightGrey};
            border-color: ${p.theme.colors.lightPink};
            cursor: pointer;
          }
          &:active {
            background-color: ${p.theme.colors.xlightGrey};
            border-color: ${p.theme.colors.lightPink};
            cursor: not-allowed;
          }
        `}
      ${p =>
        p.secondary &&
        p.bordered &&
        p.disabled &&
        css`
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};

          &:hover {
            background-color: ${p.theme.colors.lightGrey};
            border-color: ${p.theme.colors.lightGrey};
            color: ${p.theme.colors.menu};
            cursor: not-allowed;
          }
          &:active {
            background-color: ${p.theme.colors.lightGrey};
            border-color: ${p.theme.colors.lightGrey};
            color: ${p.theme.colors.menu};
            cursor: not-allowed;
          }
        `}

    ${p =>
      p.tertiary &&
      css`
        background-color: ${p.theme.colors.white};
        border-color: ${p.theme.colors.white};
        color: ${p.theme.colors.darkGrey};

        &:hover {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.darkGrey};
          cursor: pointer;
        }
        &:active {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.darkGrey};
          cursor: pointer;
          outline: none;
        }
      `}

    ${p =>
      p.tertiary &&
      p.disabled &&
      css`
        background-color: ${p.theme.colors.lightGrey};
        border-color: ${p.theme.colors.lightGrey};
        color: ${p.theme.colors.menu};

        &:hover {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};
          cursor: not-allowed;
        }
        &:active {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};
          cursor: not-allowed;
        }
      `}
      ${p =>
        p.tertiary &&
        p.bordered &&
        css`
          background-color: ${p.theme.colors.white};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.darkGrey};

          &:hover {
            background-color: ${p.theme.colors.xlightGrey};
            border-color: ${p.theme.colors.lightGrey};
            color: ${p.theme.colors.darkGrey};
            cursor: pointer;
          }
          &:active {
            background-color: ${p.theme.colors.xlightGrey};
            border-color: ${p.theme.colors.lightGrey};
            color: ${p.theme.colors.darkGrey};
            cursor: pointer;
            outline: none;
          }
        `}
      ${p =>
        p.tertiary &&
        p.bordered &&
        p.disabled &&
        css`
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};

          &:hover {
            background-color: ${p.theme.colors.lightGrey};
            border-color: ${p.theme.colors.lightGrey};
            color: ${p.theme.colors.menu};
            cursor: not-allowed;
          }
          &:active {
            background-color: ${p.theme.colors.lightGrey};
            border-color: ${p.theme.colors.lightGrey};
            color: ${p.theme.colors.menu};
            cursor: not-allowed;
          }
        `}
        ${p =>
          p.quaternary &&
          css`
            background-color: ${p.theme.colors.yellow};
            border-color: ${p.theme.colors.yellow};
            color: ${p.theme.colors.darkGrey};

            &:hover {
              background-color: ${p.theme.colors.darkYellow};
              border-color: ${p.theme.colors.darkYellow};
              color: ${p.theme.colors.darkGrey};
              cursor: pointer;
            }
            &:active {
              background-color: ${p.theme.colors.darkYellow};
              border-color: ${p.theme.colors.darkYellow};
              color: ${p.theme.colors.darkGrey};
              cursor: pointer;
              outline: none;
            }
          `}

    ${p =>
      p.quaternary &&
      p.disabled &&
      css`
        background-color: ${p.theme.colors.lightGrey};
        border-color: ${p.theme.colors.lightGrey};
        color: ${p.theme.colors.menu};

        &:hover {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};
          cursor: not-allowed;
        }
        &:active {
          background-color: ${p.theme.colors.lightGrey};
          border-color: ${p.theme.colors.lightGrey};
          color: ${p.theme.colors.menu};
          cursor: not-allowed;
        }
      `}
    ${p =>
      p.add &&
      css`
        padding: 0;
        border-radius: 100%;
        width: ${rem(p.theme.sizes.large)};
        height: ${rem(p.theme.sizes.large)};
        text-align: center;
      `}

    ${p =>
      p.withIcon &&
      css`
        display: grid;
        grid-template-columns: repeat(2, max-content);
        column-gap: ${rem(p.theme.sizes.tiny)};
        align-items: center;

        padding-top: ${rem(p.theme.sizes.none)};
        padding-bottom: ${rem(p.theme.sizes.none)};

        & ${Icon} svg {
          color: inherit;
          fill: currentColor;
        }
      `}
  `

const StyledAddText = styled(Link)`
  margin-left: ${p => rem(p.theme.sizes.base)};
  font-weight: 500;
`

const StyledBlock = styled(Block)`
  &:hover #Ellipse_112-2 {
    fill: ${p => p.theme.colors.black};
  }
  &:hover > div > a {
    color: ${p => p.theme.colors.black} !important;
  }
`

const StyledAddIcon = styled(Icon)`
  &:hover {
    cursor: pointer;
  }
`

export const AddButton: React.SFC<Props> = ({ label, onClick }) => {
  return (
    <StyledBlock marginTop="small" flex alignItems="center">
      <StyledAddIcon content={addIcon} size="xxl" onClick={onClick} />
      {label && (
        <StyledAddText onClick={onClick} color="darkGrey">
          {label}
        </StyledAddText>
      )}
    </StyledBlock>
  )
}

export const CopyButton: React.FC<Props> = ({ onClick }) => {
  return (
    <Button primary onClick={onClick} withIcon>
      <Icon content={copyIcon} color="white" size="m" />
      Copiar
    </Button>
  )
}



// WEBPACK FOOTER //
// ./src/views/components/Button/Button.tsx